import type { GetServerSideProps } from 'next';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';

const offers = {
  law: {
    path: 'law-firms',
    campaign: 'law-firms-winter-sale-2026',
    audience: 'Small law firms',
    headline: 'More time for your clients. Fewer questions on repeat.',
    introduction:
      'Help prospective clients understand your services, and help your team find the firm knowledge they need. Two assistants, grounded in the information you approve.',
    privateName: 'Firm Knowledge Dashboard',
    publicDescription:
      'Explain practice areas, office information and consultation steps. Capture contact requests and point prospective clients to your team.',
    privateDescription:
      'Give approved staff a private place to ask questions about firm procedures, onboarding instructions and internal reference documents.',
    questions: [
      'Which services does the firm offer?',
      'How can I request a consultation?',
      'Where is our approved new-client checklist?',
    ],
    boundary:
      'Your attorneys retain professional judgment. The initial setup covers approved service information and firm procedures; it does not provide legal advice, assess cases, or collect confidential case details.',
    documents: 'your public service pages and a small set of approved firm procedures',
  },
  accounting: {
    path: 'accounting-firms',
    campaign: 'accounting-firms-winter-sale-2026',
    audience: 'Accounting and bookkeeping firms',
    headline: 'Prepare for busy season with fewer repetitive questions.',
    introduction:
      'Help clients understand how to get started, and give your staff faster access to the procedures they use every day. Built around your firm’s approved information.',
    privateName: 'Staff Knowledge Dashboard',
    publicDescription:
      'Explain services, onboarding steps and approved document checklists. Direct clients to your existing secure portal and capture new-business inquiries.',
    privateDescription:
      'Help staff find internal workflows, onboarding instructions and approved answers without searching through folders or interrupting a colleague.',
    questions: [
      'Does the firm offer monthly bookkeeping?',
      'Where is the secure client portal?',
      'What does our onboarding checklist require?',
    ],
    boundary:
      'Your professionals retain responsibility for advice and filings. The initial setup does not give personalized tax or financial advice, calculate liabilities, or collect tax returns and financial account details.',
    documents: 'your public service pages and approved onboarding or staff procedures',
  },
};

export type ProfessionalVertical = keyof typeof offers;
export const getWinterSaleProps: GetServerSideProps = async () => ({
  props: { expired: Date.now() >= Date.parse('2027-01-01T05:00:00Z') },
});

export default function ProfessionalWinterSale({
  vertical,
  expired,
}: {
  vertical: ProfessionalVertical;
  expired: boolean;
}) {
  const offer = offers[vertical];
  const demoUrl = `/demo?campaign=${offer.campaign}`;
  return (
    <>
      <NextSeo
        title={
          expired
            ? `${offer.audience} | Chat.co`
            : `${offer.audience} Winter Sale — $99/month | Chat.co`
        }
        description={
          expired
            ? `Explore a public website assistant and private ${offer.privateName} for your firm. Winter Sale enrollment has ended.`
            : `A public website assistant and private ${offer.privateName} for your firm. $99 per month for the subscription duration; $0 setup. Enroll by December 31, 2026.`
        }
        canonical={`https://chat.co/winter-sale/${offer.path}`}
        noindex={expired}
      />
      <Header />
      <main className="bg-[#f7f8fc] text-slate-900">
        <section className="mx-auto grid max-w-6xl gap-10 px-6 pb-20 pt-32 lg:grid-cols-[1.25fr_1fr] lg:items-center">
          <div>
            <p className="mb-5 text-sm font-semibold uppercase tracking-widest text-indigo-700">
              {offer.audience} · Winter Sale 2026
            </p>
            <h1 className="max-w-3xl text-4xl font-semibold leading-tight tracking-tight md:text-6xl">
              {offer.headline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              {offer.introduction}
            </p>
            <Link
              href={demoUrl}
              className="mt-8 inline-block rounded-xl bg-indigo-700 px-6 py-4 font-semibold text-white hover:bg-indigo-800 focus-visible:outline focus-visible:outline-offset-4 focus-visible:outline-indigo-700">
              {expired ? 'Explore Chat.co for your firm' : 'Request a tailored demo & proposal'}
            </Link>
            <p className="mt-3 text-sm text-slate-500">
              See a working preview before deciding. No purchase required to request a demo.
            </p>
          </div>
          <aside className="rounded-3xl border border-indigo-100 bg-white p-8 shadow-xl shadow-indigo-900/5">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-700">
              Business Plan
            </p>
            {expired ? (
              <>
                <h2 className="mt-4 text-3xl font-semibold">This Winter Sale has ended.</h2>
                <p className="mt-5 leading-relaxed text-slate-600">
                  Enrollment closed on December 31, 2026. Existing subscribers keep their accepted
                  subscription terms.
                </p>
                <Link
                  href="/#pricing-brand"
                  className="mt-5 inline-block font-semibold text-indigo-700 underline">
                  View current plans
                </Link>
              </>
            ) : (
              <>
                <p className="mt-4 text-slate-500">Normally $249/month</p>
                <p className="mt-1">
                  <span className="text-6xl font-semibold tracking-tight">$99</span>
                  <span className="text-lg text-slate-500"> /month</span>
                </p>
                <p className="mt-4 font-medium">Locked in for the duration of your subscription.</p>
                <div className="my-6 border-t border-slate-100" />
                <p className="font-semibold">Waiver of the setup fee (valued at up to $2,500)</p>
                <p className="mt-2 text-slate-600">$0 setup due for the included package.</p>
                <p className="mt-6 rounded-xl bg-indigo-50 p-4 text-sm text-indigo-950">
                  Enroll by December 31, 2026, Eastern time.
                </p>
              </>
            )}
          </aside>
        </section>

        <section className="border-y border-slate-200 bg-white px-6 py-16">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-3xl font-semibold tracking-tight">
              One assistant for visitors. One for your team.
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {[
                [
                  'Public website assistant',
                  offer.publicDescription,
                  'Trained on your approved public information.',
                ],
                [
                  offer.privateName,
                  offer.privateDescription,
                  'Restricted to the people you authorize.',
                ],
              ].map(([title, description, detail]) => (
                <article key={title} className="rounded-2xl border border-slate-200 p-7">
                  <h3 className="text-xl font-semibold">{title}</h3>
                  <p className="mt-4 leading-relaxed text-slate-600">{description}</p>
                  <p className="mt-5 text-sm font-medium text-indigo-700">{detail}</p>
                </article>
              ))}
            </div>
            <p className="mt-6 max-w-4xl text-sm leading-relaxed text-slate-600">
              {offer.boundary}
            </p>
          </div>
        </section>

        <section className="mx-auto grid max-w-6xl gap-12 px-6 py-16 md:grid-cols-2">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">
              Start with questions your firm already answers.
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              We use {offer.documents}. You review the sources, wording and access before launch.
            </p>
            <ul className="mt-6 space-y-3">
              {offer.questions.map((question) => (
                <li key={question} className="rounded-xl border border-slate-200 bg-white p-4">
                  {question}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-slate-500">
              Illustrative questions. Answers depend on the sources your firm supplies.
            </p>
          </div>
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">
              A clear path from demo to launch.
            </h2>
            <ol className="mt-6 space-y-6">
              {[
                [
                  'Review your preview',
                  'See a tailored public-information assistant and a private dashboard preview. Internal examples are labeled as samples until you supply approved documents.',
                ],
                [
                  'Approve your proposal',
                  'Confirm the included setup, plan limits, sources and access. Your firm accepts the offer and completes its own subscription checkout.',
                ],
                [
                  'Launch with your team',
                  'We configure the approved knowledge, private access and standard website embed, then complete an onboarding review with you.',
                ],
              ].map(([title, text], i) => (
                <li key={title}>
                  <h3 className="font-semibold text-indigo-900">
                    {i + 1}. {title}
                  </h3>
                  <p className="mt-2 leading-relaxed text-slate-600">{text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="bg-slate-950 px-6 py-16 text-white">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-3xl font-semibold">Know what is included.</h2>
            <p className="mt-5 max-w-4xl leading-relaxed text-slate-300">
              The package includes setup of two assistants using one approved public source set and
              one approved internal source set, standard website embedding, and private access
              configuration. Business Plan message, knowledge, and team limits apply. Phone service,
              custom integrations and separately requested work are outside this package; they are
              not required to receive the setup-fee waiver.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <Link
                href={demoUrl}
                className="rounded-xl bg-white px-6 py-3 font-semibold text-slate-950">
                Request a demo & proposal
              </Link>
              <Link href="/privacy" className="self-center text-slate-200 underline">
                How private knowledge is handled
              </Link>
              <Link href="/#pricing-brand" className="self-center text-slate-200 underline">
                Business Plan limits
              </Link>
            </div>
            <p className="mt-8 text-sm text-slate-400">
              SA Cyber LLC · 99 Wall St. Ste 557, New York, NY 10005
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
